import React, { useState, useEffect } from 'react';
import { useParams, Link as RouterLink, useNavigate } from 'react-router-dom';

import {
  makeStyles,
  Typography,
  Card,
  Box,
  Container,
  Link,
  Button
} from '@material-ui/core';

import Page from 'src/shared/ui/components/Page';
import Form from 'src/shared/ui/components/Form';
import processFormErrorMessages from 'src/shared/ui/components/Form/FormErrorMessage';

import validateSchema from 'src/shared/models/SchemaValidator';
import Employee from 'src/employee/models/Employee';

import getEmployeeById from 'src/employee/use-cases/getEmployeeById';
import createEmployee from 'src/employee/use-cases/createEmployee';
import updateEmployeeById from 'src/employee/use-cases/updateEmployeeById';

import getBackofficeUserList from 'src/user/use-cases/getBackofficeUserList';

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.dark,
    minHeight: '100%',
    paddingBottom: theme.spacing(3),
    paddingTop: theme.spacing(3)
  },
  saveButton: {
    top: '-20px'
  },
  backButton: {
    top: '-20px',
    marginRight: theme.spacing(1)
  },
  scrollBox: {
    overflow: 'scroll',
    height: '80vh'
  }
}));

const EmployeeFormView = () => {
  const companyHeadquarterId = localStorage.selectedUserHeadquarter;

  const classes = useStyles();
  const [formFields, setFormFields] = useState([]);
  const [isRequesting, setIsRequesting] = useState(false);

  const { employeeId } = useParams();
  const navigate = useNavigate();

  const initFormFields = async (fields) => {
    const backofficeUserList = await getBackofficeUserList({
      companyHeadquarterId,
      page: null,
      limit: null,
      orderBy: ''
    });

    const instance = Employee({
      fields,
      backofficeUserList,
      userHeadquarterList: JSON.parse(localStorage.userHeadquarterList),
      selectedUserHeadquarter: localStorage.selectedUserHeadquarter
    });

    setFormFields(instance);
  };

  const fetchForm = async () => {
    const employee = await getEmployeeById(employeeId);

    initFormFields(employee);
  };

  useEffect(() => {
    if (employeeId === 'new') {
      initFormFields();
    } else {
      fetchForm();
    }
  }, []);

  const validateFormFields = async (formFieldsValues) => {
    const validatedFormFields = await validateSchema(formFieldsValues);
    formFieldsValues = processFormErrorMessages(
      validatedFormFields,
      formFieldsValues
    );
    setFormFields(formFieldsValues.fields);

    return {
      errors: formFieldsValues.errors,
      fields: validatedFormFields
    };
  };

  const handleChangeFormFieldsValues = (formFieldsValues) => {
    setFormFields(formFieldsValues);
  };

  const postForm = async () => {
    if (isRequesting) {
      return;
    }

    const validatedForm = await validateFormFields(formFields);
    if (validatedForm.errors) {
      alert('Rellene todos los campos correctamente.');
      return;
    }

    setIsRequesting(true);
    let result;
    if (employeeId === 'new') {
      result = await createEmployee(validatedForm.fields);
    } else {
      result = await updateEmployeeById(validatedForm.fields, employeeId);
    }
    setIsRequesting(false);
    if (typeof result.id === 'undefined') {
      alert('Se ha producido un error, vuelva a intentarlo');
    } else {
      navigate('/app/employees');
    }
  };

  return (
    <Page className={classes.root} title="Empleado">
      <Container maxWidth={false}>
        <Box className={classes.navigation} display="flex">
          <Typography color="textSecondary" variant="body2">
            <Link component={RouterLink} to="/app/employees">
              Empleados
            </Link>
            {` >  ${
              formFields.length && formFields[1].value.length
                ? `${formFields[1].value} ${formFields[2].value}`
                : 'Nuevo'
            }`}
          </Typography>
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Link component={RouterLink} to="/app/employees">
            <Button className={classes.backButton}>{'Volver a listado'}</Button>
          </Link>
          <Button
            color="primary"
            variant="contained"
            className={classes.saveButton}
            onClick={postForm}
          >
            Guardar
          </Button>
        </Box>
        <Card>
          <Form
            fields={formFields}
            onChangeFields={handleChangeFormFieldsValues}
          />
        </Card>
      </Container>
    </Page>
  );
};

export default EmployeeFormView;
